import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useState } from "react"
import { FaEdit } from "react-icons/fa"

import { useUpdateStudent } from "@/hooks/api/useStudents"
import { usePermissions } from "@/hooks/usePermissions"
import type { EditStudentProps, StudentUpdateInput } from "@/types/student"
import { studentUpdateSchema } from "@/types/student"
import { Button } from "@chakra-ui/react"

import StudentFormDialog from "./StudentFormDialog"

const EditStudent = memo(
  ({ student, onSuccess, onCancel }: EditStudentProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const updateStudentMutation = useUpdateStudent()
    const { hasPermission } = usePermissions()

    const defaultValues = useMemo<StudentUpdateInput>(
      () => ({
        first_name: student.first_name ?? "",
        last_name: student.last_name ?? "",
        date_of_birth: student.date_of_birth ?? "",
        gender: student.gender ?? null,
        household_id: student.household_id ?? "",
        current_swim_level: student.current_swim_level ?? null,
        skill_level_notes: student.skill_level_notes ?? "",
        has_asthma: student.has_asthma ?? false,
        preferred_location: student.preferred_location ?? null,
        notes: student.notes ?? "",
        is_active: student.is_active ?? false,
        photo_url: student.photo_url ?? "",
      }),
      [student],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: StudentUpdateInput) => {
      await updateStudentMutation.mutateAsync({
        studentId: student.id,
        studentData: data,
      })
      onSuccess?.()
    }

    // Don't render if user doesn't have permission
    if (!hasPermission("students:update")) {
      return null
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          aria-label={`Edit student: ${student.id}`}
        >
          <FaEdit fontSize="14px" aria-hidden="true" />
          Edit
        </Button>

        <StudentFormDialog
          isOpen={isOpen}
          title="Edit Student"
          submitLabel="Update Student"
          resolver={zodResolver(studentUpdateSchema)}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={updateStudentMutation.isPending}
          loadingText="Updating..."
        />
      </>
    )
  },
)

EditStudent.displayName = "EditStudent"

export default EditStudent
export type { EditStudentProps }
